import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Alert,
    Divider,
    Grid,
    TextField,
    styled,
    CircularProgress
} from '@mui/material';
import { 
    CloudUpload as UploadIcon,
    Person as PersonIcon,
    Event as EventIcon,
    Grade as GradeIcon,
    DragIndicator as DragIcon,
    Download as DownloadIcon
} from '@mui/icons-material';
import axios from '../services/api';
import Draggable, { DraggableEventHandler, DraggableData } from 'react-draggable';

interface DragData {
    x: number;
    y: number;
}

interface PlaceholderConfig {
    key: string;
    x: number;
    y: number;
    fontSize: number;
    fontFamily: string;
    color: string;
    alignment: 'left' | 'center' | 'right';
}

interface CertificateConfig {
    width: number;
    height: number;
    placeholders: PlaceholderConfig[];
}

const DEFAULT_PLACEHOLDERS: PlaceholderConfig[] = [
    {
        key: 'fullName',
        x: 50,
        y: 40,
        fontSize: 24,
        fontFamily: 'Arial',
        color: '#000000',
        alignment: 'left'
    },
    {
        key: 'completionDate',
        x: 50,
        y: 50,
        fontSize: 20,
        fontFamily: 'Arial',
        color: '#000000',
        alignment: 'left'
    },
    {
        key: 'result',
        x: 50,
        y: 60,
        fontSize: 20,
        fontFamily: 'Arial',
        color: '#000000',
        alignment: 'left'
    }
];

// Map of placeholder keys to display names
const PLACEHOLDER_LABELS: Record<string, string> = {
    'fullName': 'Full Name',
    'completionDate': 'Completion Date',
    'result': 'Results'
};

interface CertificateUploaderProps {
    paperId: number;
    onUploadSuccess: () => void;
    certificatePath?: string;
}

const CertificateUploader: React.FC<CertificateUploaderProps> = ({ paperId, onUploadSuccess, certificatePath }) => {
    const [excludedPlaceholders, setExcludedPlaceholders] = useState<string[]>([]);
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [config, setConfig] = useState<CertificateConfig>({
        width: 0,
        height: 0,
        placeholders: DEFAULT_PLACEHOLDERS
    });
    const [selectedPlaceholder, setSelectedPlaceholder] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [error, setError] = useState<string>('');
    const [progressOpen, setProgressOpen] = useState(false); // Progress circle modal

    const containerRef = useRef<HTMLDivElement>(null);
    const nodeRefs = useRef<{ [key: string]: React.RefObject<HTMLDivElement> }>({});

    useEffect(() => {
        config.placeholders.forEach(placeholder => {
            if (!nodeRefs.current[placeholder.key]) {
                nodeRefs.current[placeholder.key] = React.createRef();
            }
        });
    }, [config.placeholders]);

    const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget;
        setConfig(prev => ({
            ...prev,
            width: img.naturalWidth,
            height: img.naturalHeight
        }));
    };

    const handleDragStop = (key: string, data: DraggableData) => {
        setHasUnsavedChanges(true);
        const containerWidth = containerRef.current?.clientWidth || 0;
        const containerHeight = containerRef.current?.clientHeight || 0;
        
        // Convert pixel position to percentage
        const xPercent = (data.x / containerWidth) * 100;
        const yPercent = (data.y / containerHeight) * 100;
        
        setConfig(prev => ({
            ...prev,
            placeholders: prev.placeholders.map(p => 
                p.key === key 
                    ? { ...p, x: xPercent, y: yPercent }
                    : p
            )
        }));
    };

    const getPlaceholderPosition = (placeholder: PlaceholderConfig) => {
        const containerWidth = containerRef.current?.clientWidth || 0;
        const containerHeight = containerRef.current?.clientHeight || 0;
        return {
            x: (placeholder.x / 100) * containerWidth,
            y: (placeholder.y / 100) * containerHeight
        };
    };

    const handleFontSizeChange = (key: string, size: number) => {
        setConfig(prev => ({
            ...prev,
            placeholders: prev.placeholders.map(p => 
                p.key === key ? { ...p, fontSize: size } : p
            )
        }));
        setHasUnsavedChanges(true);
    };

    const handleColorChange = (key: string, color: string) => {
        setConfig(prev => ({
            ...prev,
            placeholders: prev.placeholders.map(p => 
                p.key === key ? { ...p, color } : p
            )
        }));
        setHasUnsavedChanges(true);
    };

    const handleAlignmentChange = (key: string, alignment: 'left' | 'center' | 'right') => {
        setConfig(prev => ({
            ...prev,
            placeholders: prev.placeholders.map(p =>
                p.key === key ? { ...p, alignment } : p
            )
        }));
        setHasUnsavedChanges(true);
    };

    const handleSavePositions = async () => {
        try {
            setError('');
            const formData = new FormData();
            formData.append('config', JSON.stringify({
                placeholders: config.placeholders
                    .filter(p => !excludedPlaceholders.includes(p.key))
                    .map(p => ({
                        key: p.key,
                        x: p.x,
                        y: p.y,
                        fontSize: p.fontSize,
                        color: p.color
                    }))
            }));
            
            await axios.post(`/quiz/papers/${paperId}/certificate`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setHasUnsavedChanges(false);
        } catch (error: any) {
            console.error('Failed to save placeholder positions:', error);
            if (error.response?.data?.error) {
                setError(error.response.data.error);
            } else {
                setError('Failed to save placeholder positions. Please try again.');
            }
        }
    };

    const handleUpload = async () => {
        try {
            setError('');
            if (!selectedFile) return;

            const formData = new FormData();
            formData.append('certificate', selectedFile);
            formData.append('config', JSON.stringify({
                placeholders: config.placeholders
                    .filter(p => !excludedPlaceholders.includes(p.key))
                    .map(p => ({
                        key: p.key,
                        x: p.x,
                        y: p.y,
                        fontSize: p.fontSize,
                        color: p.color
                    }))
            }));

            await axios.post(`/quiz/papers/${paperId}/certificate`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            onUploadSuccess();
            setOpen(false);
        } catch (error: any) {
            console.error('Failed to upload certificate:', error);
            if (error.response?.data?.error) {
                setError(error.response.data.error);
            } else {
                setError('Failed to upload certificate template. Please try again.');
            }
        }
    };

    const handleDownloadSample = async () => {
        setProgressOpen(true);
        try {
            setError('');
            if (!selectedFile && !certificatePath) {
                setError('Please upload a certificate template first');
                return;
            }

            if (selectedFile) {
                const formData = new FormData();
                formData.append('certificate', selectedFile);
                formData.append('config', JSON.stringify({
                    placeholders: config.placeholders
                        .filter(p => !excludedPlaceholders.includes(p.key))
                        .map(p => ({
                            key: p.key,
                            x: p.x,
                            y: p.y,
                            fontSize: p.fontSize,
                            color: p.color,
                            alignment: p.alignment
                        }))
                }));

                await axios.post(`/quiz/papers/${paperId}/certificate`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                onUploadSuccess();
            }

            // Now get the sample
            try {
                const response = await axios.post(
                    `/quiz/papers/${paperId}/certificate/sample`,
                    {},
                    { responseType: 'blob' }
                );
                // Hide progress as soon as download is about to start
                setProgressOpen(false);
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'certificate_sample.pdf');
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } catch (error: any) {
                setProgressOpen(false);
                if (error.response?.data) {
                    // Try to read the error message from the blob
                    const reader = new FileReader();
                    reader.onload = () => {
                        try {
                            const errorData = JSON.parse(reader.result as string);
                            setError(errorData.error || 'Failed to download sample certificate');
                        } catch {
                            setError('Failed to download sample certificate');
                        }
                    };
                    reader.readAsText(error.response.data);
                } else {
                    setError('Failed to download sample certificate');
                }
            }
        } catch (error: any) {
            console.error('Error handling certificate:', error);
            if (error.response?.data?.error) {
                setError(error.response.data.error);
            } else {
                setError('Failed to handle certificate operation');
            }
        } finally {
            setProgressOpen(false);
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (file.type === 'image/jpeg' || file.type === 'image/png') {
                setSelectedFile(file);
                const url = URL.createObjectURL(file);
                setPreviewUrl(url);
            } else {
                setError('Please select a JPG or PNG file');
            }
        }
    };

    return (
        <Box>
            <Dialog open={progressOpen} PaperProps={{ sx: { pointerEvents: 'auto' } }} disableEscapeKeyDown>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
                    <Box sx={{ mb: 2 }}>
                        <CircularProgress size={48} />
                    </Box>
                    <Typography variant="body1">{t('contentCreator.certificate.generatingSample') || 'Generating sample, please wait...'}</Typography>
                </DialogContent>
            </Dialog>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => setOpen(true)}
                size="small"
                sx={{ 
                    py: 0.5,
                    minHeight: '24px',
                    lineHeight: 1,
                    width: '110px',
                    backgroundColor: certificatePath ? 'success.lighter' : 'error.lighter',
                    borderColor: certificatePath ? 'success.light' : 'error.light',
                    color: certificatePath ? 'success.dark' : 'error.dark',
                    '&:hover': {
                        backgroundColor: certificatePath ? 'success.light' : 'error.light',
                        borderColor: certificatePath ? 'success.main' : 'error.main',
                        color: certificatePath ? 'success.darker' : 'error.darker',
                    }
                }}
            >
                {t('contentCreator.certificate.uploadButton')}
            </Button>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{t('contentCreator.certificate.uploadButton')}</DialogTitle>
                <DialogContent>
                    {/* Header controls: Upload + Placeholders row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minHeight: 48, position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper', pb: 1, pt: 1, borderBottom: 1, borderColor: 'divider' }}>
                        <input
                            accept=".jpg,.jpeg,.png"
                            style={{ display: 'none' }}
                            id="certificate-file"
                            type="file"
                            onChange={handleFileSelect}
                        />
                        <label htmlFor="certificate-file">
                            <Button variant="contained" component="span">
                                {t('contentCreator.certificate.upload')}
                            </Button>
                        </label>
                        {/* Placeholders to include */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ mr: 1 }}>Placeholders to include:</Typography>
                            {config.placeholders.map(p => (
                                <label key={p.key} style={{ display: 'flex', alignItems: 'center', marginRight: 8 }}>
                                    <input
                                        type="checkbox"
                                        checked={!excludedPlaceholders.includes(p.key)}
                                        onChange={e => {
                                            setExcludedPlaceholders((prev: string[]) =>
                                                e.target.checked
                                                    ? prev.filter((k: string) => k !== p.key)
                                                    : [...prev, p.key]
                                            );
                                        }}
                                    />
                                    <span style={{ marginLeft: 4 }}>{PLACEHOLDER_LABELS[p.key] || p.key}</span>
                                </label>
                            ))}
                        </Box>
                    </Box>
                    {/* Font controls row */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, minHeight: 64, position: 'sticky', top: 56, zIndex: 2, bgcolor: 'background.paper', pb: 1, pt: 1, borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                        <TextField
                            type="number"
                            label="Font Size"
                            value={selectedPlaceholder ? config.placeholders.find(p => p.key === selectedPlaceholder)?.fontSize ?? '' : ''}
                            onChange={e => {
                                if (selectedPlaceholder) handleFontSizeChange(selectedPlaceholder, Number(e.target.value));
                            }}
                            size="small"
                            sx={{ width: '80px' }}
                            disabled={!selectedPlaceholder}
                        />
                        <TextField
                            type="color"
                            label="Font Color"
                            value={selectedPlaceholder ? config.placeholders.find(p => p.key === selectedPlaceholder)?.color ?? '#000000' : '#000000'}
                            onChange={e => {
                                if (selectedPlaceholder) handleColorChange(selectedPlaceholder, e.target.value);
                            }}
                            size="small"
                            sx={{ width: '130px', p: 0 }}
                            disabled={!selectedPlaceholder}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            select
                            label="Alignment"
                            value={selectedPlaceholder ? config.placeholders.find(p => p.key === selectedPlaceholder)?.alignment ?? 'left' : 'left'}
                            onChange={e => {
                                if (selectedPlaceholder) handleAlignmentChange(selectedPlaceholder, e.target.value as 'left' | 'center' | 'right');
                            }}
                            size="small"
                            sx={{ width: '110px' }}
                            SelectProps={{ native: true }}
                            disabled={!selectedPlaceholder}
                        >
                            <option value="left">Left</option>
                            <option value="center">Center</option>
                            <option value="right">Right</option>
                        </TextField>
                    </Box>

                    {previewUrl && (
                        <>
                            <Box sx={{ position: 'relative', width: '100%', mt: 2 }} ref={containerRef}>
                                <Box
                                    component="img"
                                    src={previewUrl}
                                    alt="Certificate Preview"
                                    onLoad={handleImageLoad}
                                    sx={{
                                        display: 'block',
                                        maxWidth: '100%',
                                        height: 'auto',
                                        minWidth: 'auto',
                                        minHeight: 'auto',
                                        maxHeight: '1000px',
                                        objectFit: 'contain',
                                        margin: '0 auto',
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%'
                                    }}
                                >
                                    {config.placeholders.filter(p => !excludedPlaceholders.includes(p.key)).map((placeholder) => (
                                        <Draggable
                                            key={placeholder.key}
                                            axis="both"
                                            handle=".drag-handle"
                                            bounds="parent"
                                            position={getPlaceholderPosition(placeholder)}
                                            onStop={(e, data) => handleDragStop(placeholder.key, data)}
                                            nodeRef={nodeRefs.current[placeholder.key]}
                                            onStart={() => setSelectedPlaceholder(placeholder.key)}
                                        >
                                            <Box
                                                ref={nodeRefs.current[placeholder.key]}
                                                position="absolute"
                                                className="drag-handle"
                                                sx={{
                                                    cursor: 'move',
                                                    userSelect: 'none',
                                                    border: '1px dashed',
                                                    borderColor: selectedPlaceholder === placeholder.key ? 'primary.main' : 'grey.400',
                                                    p: 1,
                                                    bgcolor: selectedPlaceholder === placeholder.key ? 'rgba(25, 118, 210, 0.1)' : 'background.paper',
                                                    borderRadius: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}
                                            >
                                                <DragIcon fontSize="small" />
                                                <Typography 
                                                    variant="body2" 
                                                    sx={{ 
                                                        fontSize: `${placeholder.fontSize}px`,
                                                        lineHeight: 1,
                                                        color: placeholder.color,
                                                        textAlign: placeholder.alignment,
                                                        width: '100%',
                                                        minWidth: 60
                                                    }}
                                                >
                                                    {PLACEHOLDER_LABELS[placeholder.key] || placeholder.key}
                                                </Typography>

                                            </Box>
                                        </Draggable>
                                    ))}
                                </Box>
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>{t('contentCreator.certificate.cancel')}</Button>
                    {previewUrl && (
                        <>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSavePositions}
                                disabled={!hasUnsavedChanges}
                            >
                                {t('contentCreator.certificate.savePositions')}
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleDownloadSample}
                                disabled={hasUnsavedChanges}
                                startIcon={<DownloadIcon />}
                            >
                                {t('contentCreator.certificate.downloadSample')}
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleUpload}
                                disabled={!selectedFile}
                            >
                                {t('contentCreator.certificate.upload')}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CertificateUploader;
